import React, { useCallback, useEffect, useRef, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement } from '@stripe/react-stripe-js'
import {
  AppDialog,
  AppDialogContent,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogBody,
  AppDialogFooter,
  AppButton,
  AppLink,
  useToastV2
} from '@easyllama/ui-components'
import { LlamaAlertTriangle } from '@easyllama/ui-components/icons'
import { useApp } from '~/context/appContext'
import { fetchSetupIntent } from '~/service/Billing/fetchApiClient'
import { useSubmitPaymentMethod } from '~/hooks/useSubmitPaymentMethod'

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

// useStripe/useElements only work inside <Elements>, so the submit UI has to be its
// own component rendered as a child of Elements below — same reason AddPaymentMethodDialog
// and AddPaymentMethodForm are split into two files.
// AppDialogBody is its own independent scroll region (flex-1 min-h-0 overflow-y-auto) -
// EDS dialogs use exactly one per dialog, so the payment element joins the same body as
// the rest of the content instead of getting a second scrollbar.
const StripeCardBody = ({ onSetupIntentConsumed, children, footerNote }) => {
  const { handleSubmit, isSubmitting, stripe } = useSubmitPaymentMethod({
    onSuccess: () => window.location.reload(),
    onSetupIntentConsumed
  })

  return (
    <>
      <AppDialogBody className='flex flex-col gap-4'>
        {children}
        <PaymentElement options={{ wallets: { applePay: 'never', googlePay: 'never', link: 'never' } }} />
        {footerNote}
      </AppDialogBody>
      <AppDialogFooter>
        <AppButton onClick={handleSubmit} disabled={!stripe || isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Submit'}
        </AppButton>
      </AppDialogFooter>
    </>
  )
}

const RequestNewPaymentMethodModal = ({ balanceDue, autoRenew, openInvoices }) => {
  const toast = useToastV2()
  const { company, jwt, csrf } = useApp()
  const [stripePromise, setStripePromise] = useState(null)
  const [clientSecret, setClientSecret] = useState(null)
  const [setupIntentStatus, setSetupIntentStatus] = useState('loading')
  const [elementsKey, setElementsKey] = useState(0)
  // Mount, "Try again", and onSetupIntentConsumed can each trigger a call, so a slower
  // stale response must not be allowed to overwrite a newer one's result.
  const requestIdRef = useRef(0)

  const loadSetupIntent = useCallback(async () => {
    const requestId = ++requestIdRef.current
    setSetupIntentStatus('loading')
    try {
      const { data } = await fetchSetupIntent(company.id, jwt, csrf)
      if (requestIdRef.current !== requestId) return
      setStripePromise(loadStripe(data.publishableKey))
      setClientSecret(data.clientSecret)
      setSetupIntentStatus('ready')
    } catch (err) {
      if (requestIdRef.current !== requestId) return
      toast.critical(err?.message || 'Failed to initialize payment form', undefined, { showCloseButton: false })
      setSetupIntentStatus('error')
    }
  }, [company.id, jwt, csrf])

  useEffect(() => {
    loadSetupIntent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSetupIntentConsumed = () => {
    setElementsKey((key) => key + 1)
    loadSetupIntent()
  }

  const title = autoRenew ? 'Your account balance is low' : 'Late invoice'
  // balanceDue is null when the backend couldn't determine the exact amount (e.g. a
  // billing-service error) - fall back to generic copy rather than assert a wrong figure.
  const formattedBalance = balanceDue != null ? currencyFormatter.format(balanceDue) : null
  const supportEmail = autoRenew ? 'support@easyllama.com' : 'billing@easyllama.com'

  const bodyContent = (
    <>
      <p className='text-sm leading-[150%] text-llama-grey-7 lining-nums'>
        {autoRenew ? (
          <>
            A{formattedBalance && ` ${formattedBalance}`} balance is due for past usage on your account.{' '}
            <strong>Please update your credit card on file to regain access to your dashboard.</strong>
          </>
        ) : (
          <>
            We have noticed that a payment{formattedBalance && ` for ${formattedBalance}`} is outstanding on
            your account, leading to an interruption in your service.{' '}
            <strong>To restore access and continue enjoying our services, please settle your outstanding
            invoice as soon as possible.</strong>
          </>
        )}
      </p>
      {openInvoices.length > 0 && (
        <ul className='flex flex-col gap-1.5'>
          {openInvoices.map((invoice) => (
            <li key={invoice.id} className='text-sm'>
              <AppLink
                href={invoice.receiptUrl}
                target='_blank'
                rel='noreferrer'
                text={`Click here to open invoice ${invoice.id}`}
              />
            </li>
          ))}
        </ul>
      )}
      <p className='text-sm leading-[150%] text-llama-grey-7'>
        {autoRenew
          ? 'Please add a payment method below:'
          : 'For immediate access, you can securely add a payment method below:'}
      </p>
      {setupIntentStatus === 'loading' && (
        <p className='text-sm text-llama-grey-5'>Loading payment form...</p>
      )}
      {setupIntentStatus === 'error' && (
        <p className='text-sm text-llama-grey-7'>
          We could not load the payment form.{' '}
          <AppLink onClick={loadSetupIntent} text='Try again' />
        </p>
      )}
    </>
  )

  // Support contact renders last (after the payment fields) per design feedback - it now
  // reads as a footnote rather than competing with the balance/payment instructions above it.
  const supportContact = (
    <p className='text-sm leading-[150%] text-llama-grey-7'>
      If you have any questions, please contact us at <AppLink href={`mailto:${supportEmail}`} text={supportEmail} />
    </p>
  )

  return (
    <AppDialog open>
      <AppDialogContent showCloseButton={false} className='request-new-payment-method-preview text-llama-grey-7'>
        <AppDialogHeader>
          <div className='flex items-center gap-2'>
            <LlamaAlertTriangle aria-hidden='true' className='size-6 shrink-0 text-llama-red-5' />
            <AppDialogTitle className='!text-llama-red-5'>{title}</AppDialogTitle>
          </div>
        </AppDialogHeader>
        {setupIntentStatus === 'ready' ? (
          <Elements key={elementsKey} stripe={stripePromise} options={{ clientSecret, wallets: { link: 'never' } }}>
            <StripeCardBody onSetupIntentConsumed={handleSetupIntentConsumed} footerNote={supportContact}>
              {bodyContent}
            </StripeCardBody>
          </Elements>
        ) : (
          <AppDialogBody className='flex flex-col gap-4'>
            {bodyContent}
            {supportContact}
          </AppDialogBody>
        )}
      </AppDialogContent>
    </AppDialog>
  )
}

export default RequestNewPaymentMethodModal
